import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from '../authentication.service';
import { InitializationStatusService } from '../../../api/gen';
import { map } from 'rxjs/operators';
import { InitializationStatus } from '../../model/initialization-status';
import { RoleService } from '../role.service';
import { BackendRole } from '../backend-role';
import { sentryLogger } from 'shared/core';
import { AuthRedirectState } from '../authentication.service';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AuthCallbackComponent implements OnInit {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthenticationService,
    private readonly roleService: RoleService,
    private readonly initializationStatusService: InitializationStatusService,
  ) {}

  async ngOnInit() {
    let user;
    try {
      user = await this.authService.authCallback();
    } catch (error) {
      sentryLogger.warn('Auth callback failed, redirecting to login', {
        error: error,
      });
      await this.router.navigate(['/']);
      return;
    }

    const postLoginPath = this.getPostLoginPath(user?.state);
    if (postLoginPath) {
      await this.router.navigateByUrl(postLoginPath, {
        replaceUrl: true,
      });
      return;
    }

    if (this.roleService.hasRole(BackendRole.Admin)) {
      await this.checkInitializationStatus();
    } else {
      await this.navigateToHome();
    }
  }

  private async checkInitializationStatus() {
    this.initializationStatusService
      .initializationGet()
      .pipe(map((dto) => InitializationStatus.fromDto(dto)))
      .subscribe(async (initializationStatus) => {
        if (
          initializationStatus.companySettingsInitialized === false ||
          initializationStatus.inquiryReportTemplateInitialized === false
        ) {
          await this.router.navigate(['setup']);
        } else {
          await this.navigateToHome();
        }
      });
  }

  private async navigateToHome() {
    await this.router.navigate(['video-appointments']);
  }

  private getPostLoginPath(state: unknown): string | null {
    const postLoginPath = (state as AuthRedirectState | undefined)
      ?.postLoginPath;
    if (!postLoginPath?.startsWith('/')) {
      return null;
    }

    return postLoginPath;
  }
}
